/**
 * NDATermsConditions — per-NDA Terms & Conditions editor.
 *
 * 2026-02-25 — Configurable Terms & Conditions + Pre-Approval PDF Preview
 * enhancement.  Editing here mutates ONLY this NDA's working copy — the
 * Template Master and other NDAs are never touched.  Edits are refused
 * once the NDA leaves Draft.
 *
 * Props:
 *   nda          Full NDA record from GET /billing/nda/{id}.
 *   canEdit      Boolean — parent-computed (Draft + nda:write).
 *   onChange(n)  Called with the updated NDA after any CRUD mutation.
 */
import React, { useMemo, useState } from "react";
import {
    PencilSimple, TrashSimple, Plus, ArrowUp, ArrowDown, ArrowsClockwise,
    Check, X, Eye,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import FormDialog, { FormDialogCancel } from "../FormDialog";
import { DialogFooter } from "../ui/dialog";

function ClauseEditor({ open, onOpenChange, clause, onSave }) {
    const [heading, setHeading] = useState("");
    const [body, setBody] = useState("");
    const [included, setIncluded] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    React.useEffect(() => {
        if (open) {
            setHeading(clause?.heading || "");
            setBody(clause?.body || "");
            setIncluded(clause?.is_included !== false);
        }
    }, [open, clause]);

    const isDirty =
        (clause?.heading || "") !== heading ||
        (clause?.body || "") !== body ||
        (clause?.is_included !== false) !== included;
    const isAdd = !clause?.clause_id;

    const submit = async () => {
        setSubmitting(true);
        try {
            const payload = isAdd
                ? { heading, body, is_included: included, mandatory: false }
                : {
                    ...(heading !== (clause.heading || "") ? { heading } : {}),
                    ...(body !== (clause.body || "") ? { body } : {}),
                    ...(included !== (clause.is_included !== false) ? { is_included: included } : {}),
                };
            await onSave(payload);
            onOpenChange(false);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            isDirty={isDirty}
            title={isAdd ? "Add Clause" : "Edit Clause"}
            description={isAdd
                ? "Add a bespoke clause to this NDA.  The Template Master is not affected."
                : "Edit this clause on THIS NDA only.  The Template Master and other NDAs are not affected."
            }
            testId="nda-clause-dialog"
            widthClass="max-w-2xl"
        >
            <div className="space-y-3">
                <div>
                    <Label>Heading *</Label>
                    <Input
                        value={heading}
                        onChange={(e) => setHeading(e.target.value)}
                        maxLength={160}
                        placeholder="e.g. Confidentiality Obligations"
                        data-testid="nda-clause-heading"
                    />
                </div>
                <div>
                    <Label>Body *</Label>
                    <Textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={10}
                        maxLength={8000}
                        placeholder="Full clause wording…"
                        data-testid="nda-clause-body"
                    />
                    <div className="text-[11px] text-slate-500 mt-1">
                        {body.length} / 8000 characters
                    </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={included}
                        onChange={(e) => setIncluded(e.target.checked)}
                        data-testid="nda-clause-included"
                    />
                    Include this clause in the rendered NDA
                </label>
            </div>
            <DialogFooter>
                <FormDialogCancel data-testid="nda-clause-cancel">Cancel</FormDialogCancel>
                <Button
                    onClick={submit}
                    disabled={submitting || !heading.trim() || !body.trim() || (!isAdd && !isDirty)}
                    data-testid="nda-clause-submit"
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                >
                    {submitting ? "Saving…" : isAdd ? "Add clause" : "Save changes"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}

export default function NDATermsConditions({ nda, canEdit, onChange }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingClause, setEditingClause] = useState(null);
    const [busy, setBusy] = useState(false);
    const [latestPublished, setLatestPublished] = useState(null);

    // Look up the currently-Published template of this NDA's type so the
    // "Upgrade Template to Latest" action can show a version diff and be
    // hidden when the Draft is already on the latest version.
    React.useEffect(() => {
        if (!canEdit || !nda?.nda_type) { setLatestPublished(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(
                    `/billing/nda-template/list?nda_type=${nda.nda_type}&include_inactive=false`,
                );
                const pub = (data?.items || []).find((t) => t.is_active && t.status === "Published");
                if (!cancelled) setLatestPublished(pub || null);
            } catch (err) {
                if (!cancelled) setLatestPublished(null);
            }
        })();
        return () => { cancelled = true; };
    }, [nda?.nda_type, canEdit, nda?.template_snapshot?.template_id]);

    const currentTplId = nda?.template_snapshot?.template_id;
    const currentTplVersion = nda?.template_snapshot?.version;
    const canUpgrade = Boolean(
        canEdit && latestPublished && currentTplId && latestPublished.id !== currentTplId,
    );

    const sorted = useMemo(
        () => [...(nda?.clauses || [])].sort(
            (a, b) => (a.display_order || 100) - (b.display_order || 100),
        ),
        [nda?.clauses],
    );
    const includedCount = sorted.filter((c) => c.is_included !== false).length;

    const openAdd = () => {
        setEditingClause(null);
        setDialogOpen(true);
    };
    const openEdit = (c) => {
        setEditingClause(c);
        setDialogOpen(true);
    };

    const saveDialog = async (payload) => {
        try {
            const url = editingClause?.clause_id
                ? `/billing/nda/${nda.id}/clauses/${editingClause.clause_id}`
                : `/billing/nda/${nda.id}/clauses/add`;
            const method = editingClause?.clause_id ? "patch" : "post";
            const { data } = await api[method](url, payload);
            onChange(data);
            toast.success(editingClause?.clause_id ? "Clause updated" : "Clause added");
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
            throw err;
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`Remove clause "${c.heading}"?`)) return;
        setBusy(true);
        try {
            const { data } = await api.delete(`/billing/nda/${nda.id}/clauses/${c.clause_id}`);
            onChange(data);
            toast.success("Clause removed");
        } catch (err) {
            toast.error(formatApiError(err, "Remove failed"));
        } finally {
            setBusy(false);
        }
    };

    const toggleInclude = async (c) => {
        setBusy(true);
        try {
            const { data } = await api.patch(
                `/billing/nda/${nda.id}/clauses/${c.clause_id}`,
                { is_included: !(c.is_included !== false) },
            );
            onChange(data);
        } catch (err) {
            toast.error(formatApiError(err, "Update failed"));
        } finally {
            setBusy(false);
        }
    };

    const reorder = async (idx, direction) => {
        const target = idx + direction;
        if (target < 0 || target >= sorted.length) return;
        const newOrder = [...sorted];
        [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
        setBusy(true);
        try {
            const { data } = await api.post(
                `/billing/nda/${nda.id}/clauses/reorder`,
                { ordered_clause_ids: newOrder.map((c) => c.clause_id) },
            );
            onChange(data);
        } catch (err) {
            toast.error(formatApiError(err, "Reorder failed"));
        } finally {
            setBusy(false);
        }
    };

    const restoreTemplate = async () => {
        if (!window.confirm(
            "Restore the standard Terms & Conditions from the selected NDA template?\n\n"
            + "This DISCARDS all clause edits, added clauses, exclusions and reorderings "
            + "on this NDA and reloads the template's default clauses.  This cannot be undone.",
        )) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/billing/nda/${nda.id}/clauses/restore-template`);
            onChange(data);
            toast.success("Template defaults restored");
        } catch (err) {
            toast.error(formatApiError(err, "Restore failed"));
        } finally {
            setBusy(false);
        }
    };

    const upgradeToLatestPublished = async () => {
        if (!latestPublished) return;
        const toLabel = `${latestPublished.template_name} · v${latestPublished.version}`;
        const fromLabel = `${nda?.template_snapshot?.template_name || nda?.template_snapshot?.name || "current template"} · v${currentTplVersion || "?"}`;
        if (!window.confirm(
            `Upgrade this Draft NDA from ${fromLabel} to the currently Published ${toLabel}?\n\n`
            + "This WILL replace the entire Terms & Conditions working copy with the "
            + "latest Published template's clauses.  ANY MANUAL EDITS, added clauses, "
            + "exclusions and reorderings on this Draft will be lost.\n\n"
            + "The upgrade affects ONLY this Draft NDA.  No other NDA and no historical "
            + "record is touched.  This cannot be undone.",
        )) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/billing/nda/${nda.id}/clauses/upgrade-to-published`);
            onChange(data);
            toast.success(`Draft upgraded to ${toLabel}`);
        } catch (err) {
            toast.error(formatApiError(err, "Upgrade failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="reedos-card p-5" data-testid="nda-tnc-card">
            <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
                <div>
                    <div className="reedos-eyebrow" data-testid="nda-tnc-heading">
                        Terms &amp; Conditions ({sorted.length})
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                        Working copy — cloned from the selected NDA template.  Edits stay
                        on THIS NDA only; the Template Master is untouched.
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                        {includedCount} of {sorted.length} included in rendered NDA
                    </div>
                    {canUpgrade && (
                        <div className="text-[11px] text-amber-800 mt-1" data-testid="nda-tnc-upgrade-notice">
                            This Draft is on {nda?.template_snapshot?.template_name || "an older template"} · v{currentTplVersion}.
                            The currently Published template is <strong>{latestPublished?.template_name} · v{latestPublished?.version}</strong>.
                            Use <em>Upgrade Template to v{latestPublished?.version}</em> to opt in — no other NDA is affected.
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <a
                        href={`/ndas/${nda.id}/print`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 h-8 px-3 text-xs rounded border border-slate-200 text-slate-700 hover:bg-slate-50"
                        data-testid="nda-tnc-preview-btn"
                    >
                        <Eye size={14} /> Preview NDA
                    </a>
                    {canEdit && (
                        <>
                            {canUpgrade && (
                                <button
                                    type="button"
                                    onClick={upgradeToLatestPublished}
                                    disabled={busy}
                                    className="inline-flex items-center gap-1.5 h-8 px-3 text-xs rounded border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                                    data-testid="nda-tnc-upgrade-btn"
                                    title={`Upgrade this Draft to ${latestPublished?.template_name} · v${latestPublished?.version}`}
                                >
                                    <ArrowsClockwise size={14} /> Upgrade Template to v{latestPublished?.version}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={restoreTemplate}
                                disabled={busy}
                                className="inline-flex items-center gap-1.5 h-8 px-3 text-xs rounded border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                data-testid="nda-tnc-restore-btn"
                            >
                                <ArrowsClockwise size={14} /> Restore Template Defaults
                            </button>
                            <Button
                                onClick={openAdd}
                                disabled={busy}
                                className="bg-slate-900 hover:bg-slate-800 text-white h-8 px-3 text-xs gap-1.5"
                                data-testid="nda-tnc-add-btn"
                            >
                                <Plus size={14} /> Add Clause
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {sorted.length === 0 ? (
                <div className="text-center text-sm text-slate-500 py-6" data-testid="nda-tnc-empty">
                    No clauses configured.  Restore template defaults or add clauses manually.
                </div>
            ) : (
                <div className="space-y-2" data-testid="nda-tnc-list">
                    {sorted.map((c, idx) => {
                        const included = c.is_included !== false;
                        return (
                            <div
                                key={c.clause_id}
                                className={
                                    "rounded-md border transition-colors " +
                                    (included
                                        ? "border-slate-200 bg-white"
                                        : "border-slate-200 bg-slate-50 opacity-70")
                                }
                                data-testid={`nda-tnc-row-${c.clause_id}`}
                            >
                                <div className="flex items-start gap-3 px-3 py-2.5">
                                    <div className="text-xs text-slate-400 font-mono pt-1 w-6 shrink-0">
                                        {idx + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-semibold text-slate-900">
                                                {c.heading}
                                            </span>
                                            {c.mandatory && (
                                                <span className="text-[9px] font-bold uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5">
                                                    Mandatory
                                                </span>
                                            )}
                                            {!included && (
                                                <span className="text-[9px] font-bold uppercase tracking-wide bg-slate-100 text-slate-600 border border-slate-200 rounded px-1.5 py-0.5" data-testid={`nda-tnc-excluded-${c.clause_id}`}>
                                                    Excluded
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-slate-600 mt-1 whitespace-pre-line line-clamp-4">
                                            {c.body}
                                        </div>
                                    </div>
                                    {canEdit && (
                                        <div className="flex items-center gap-1 shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => reorder(idx, -1)}
                                                disabled={busy || idx === 0}
                                                className="p-1 text-slate-500 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed rounded hover:bg-slate-100"
                                                title="Move up"
                                                data-testid={`nda-tnc-up-${c.clause_id}`}
                                            >
                                                <ArrowUp size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => reorder(idx, +1)}
                                                disabled={busy || idx === sorted.length - 1}
                                                className="p-1 text-slate-500 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed rounded hover:bg-slate-100"
                                                title="Move down"
                                                data-testid={`nda-tnc-down-${c.clause_id}`}
                                            >
                                                <ArrowDown size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => toggleInclude(c)}
                                                disabled={busy}
                                                className={
                                                    "p-1 rounded hover:bg-slate-100 " +
                                                    (included ? "text-emerald-600" : "text-slate-400")
                                                }
                                                title={included ? "Exclude from rendered NDA" : "Include in rendered NDA"}
                                                data-testid={`nda-tnc-toggle-${c.clause_id}`}
                                            >
                                                {included ? <Check size={14} weight="bold" /> : <X size={14} weight="bold" />}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => openEdit(c)}
                                                disabled={busy}
                                                className="p-1 text-slate-500 hover:text-slate-900 rounded hover:bg-slate-100"
                                                title="Edit clause"
                                                data-testid={`nda-tnc-edit-${c.clause_id}`}
                                            >
                                                <PencilSimple size={14} />
                                            </button>
                                            {!c.mandatory && (
                                                <button
                                                    type="button"
                                                    onClick={() => remove(c)}
                                                    disabled={busy}
                                                    className="p-1 text-rose-600 hover:text-rose-800 rounded hover:bg-rose-50"
                                                    title="Remove clause"
                                                    data-testid={`nda-tnc-remove-${c.clause_id}`}
                                                >
                                                    <TrashSimple size={14} />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <ClauseEditor
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                clause={editingClause}
                onSave={saveDialog}
            />
        </div>
    );
}
